import { useAdminFeatureFlags, useSetFeatureFlag } from '../../../hooks/admin/useCatchAllAlerts'

/**
 * Runtime on/off for each alert phase.
 *
 * These map to rows in feature_flags, which the processor re-reads on every
 * 30-second orchestrator tick - so a toggle here takes effect without a deploy.
 *
 * Only alert phases are listed. The same table also carries flags that govern
 * ingestion (service_bus_dual_write) and the global alerting kill switch
 * (alert_notifications); the API refuses those deliberately, so a mis-click
 * here can never touch the message pipeline.
 */
export default function FeatureFlagsPage() {
  const { data: flags, isLoading, isError } = useAdminFeatureFlags()
  const setFlag = useSetFeatureFlag()

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold">Alert Phases</h1>
        <p className="text-gray-600 mt-1">
          Turn each kind of alert processing on or off. Changes are picked up by the processor
          within 30 seconds — no deployment needed.
        </p>
      </div>

      {isLoading && <p className="text-gray-600">Loading…</p>}
      {isError && <p className="text-red-600">Could not load feature flags.</p>}

      {flags && (
        <section className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
          {flags.map((flag) => (
            <div key={flag.feature} className="p-4 flex items-start gap-4">
              <label className="flex items-center cursor-pointer pt-0.5">
                <input
                  type="checkbox"
                  checked={flag.enabled}
                  disabled={setFlag.isPending}
                  onChange={(e) =>
                    setFlag.mutate({ feature: flag.feature, enabled: e.target.checked })
                  }
                  className="rounded border-gray-300 h-5 w-5"
                />
              </label>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{flag.label}</span>
                  <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                    {flag.feature}
                  </code>
                  {!flag.exists && (
                    <span className="text-xs text-gray-500">not yet configured</span>
                  )}
                </div>
                <p className="text-gray-600 text-sm mt-1">{flag.description}</p>
              </div>
              <span
                className={`text-xs font-medium px-2 py-1 rounded shrink-0 ${
                  flag.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {flag.enabled ? 'ON' : 'OFF'}
              </span>
            </div>
          ))}
        </section>
      )}

      <p className="text-sm text-gray-500">
        A phase also needs its own switch on the <strong>Settings</strong> page, and at least one
        enabled recipient, before anything is delivered.
      </p>
    </div>
  )
}
